/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ShowVarientRequestDetails.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import CustomDataGridV2 from "../common/datagrid/CustomDataGridV2";
import DoneIcon from "@mui/icons-material/Done";
import BlockIcon from "@mui/icons-material/Block";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useDispatch } from "react-redux";
import CustomDialogBoxV2 from "../common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../common/textField/CustomFormTextFieldV2";
import { HandleApiActions } from "../../../redux/actions";
import editRequestAPI from "../../../redux/actions/editRequestApi";

function ShowVarientRequestDetails({
  editRequestCount,
  page,
  rowsPerPage,
  tableRowsData,
  fetchEditRequests,
  handleRowPerPageER,
}) {
  const [selectedAddVcId, setSelectedAddVcId] = useState(null);
  const [addReason, setAddReason] = useState("");
  const [openAddVcRejectDialog, setOpenAddVcRejectDialog] = useState(false);
  const [openAddVcApproveDialog, setOpenAddVcApproveDialog] = useState(false);

  const dispatch = useDispatch();

  const [expandedRows, setExpandedRows] = useState({});

  const handleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleApproveAddVcRequest = (params) => {
    setSelectedAddVcId(params);
    setOpenAddVcApproveDialog(true);
  };
  const approveRequestAccess = async ({ params, reason }) => {
    console.log("paramsparamsApprove", params);
    let apiDetails = editRequestAPI.approve_edit_requests;

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            er_id: Number(params?.id),
            reason: reason,
          },
          show_toast: true,
        })
      )
    );

    if (!resp.error) {
      fetchEditRequests({
        project_id: Number.parseInt(params?.project_id, 10),
        variant_id: Number.parseInt(params?.variant_id, 10),
        page_number: 1,
        page_size: 25,
      });
      setOpenAddVcApproveDialog(false);
    }
  };
  const rejectRequestAccess = async ({
    params,
    reason,
  }) => {
    let apiDetails = editRequestAPI.reject_edit_requests;

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            er_id: Number(params?.id),
            reason: reason,
          },
          show_toast: true,
        })
      )
    );

    if (!resp.error) {
      fetchEditRequests({
        project_id: params?.project_id,
        variant_id: params?.variant_id,
        page_number: 1,
        page_size: 25,
      });
      setOpenAddVcRejectDialog(false);
    }
  };
  const handleRejectAddVcRequest = (params) => {
    setSelectedAddVcId(params);
    setOpenAddVcRejectDialog(true);
  };
  const COLUMNS = [
    {
      field: "expand",
      headerName: "",
      flex: 0.2,
      minWidth: 40,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;

        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowExpansion(params.row.id);
            }}
            sx={{ p: 0.5 }}
          >
            {expandedRows[params.row.id] ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      field: "module_type",
      headerName: "Module Type",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        console.log("--params--Variant Request Details", params);
        if (params.row.isDetailRow) {
          const data = params.row.parentData;
          return (
            <Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Requested By:
                </Typography>
                <Typography variant="body2">
                  Name: {data?.requested_user_details?.fullname || "-"}
                </Typography>
                <Typography variant="body2">
                  Email: {data?.requested_user_details?.email || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  Requested Reason:
                </Typography>
                <Typography variant="body2">
                  {data?.requested_reason || "-"}
                </Typography>
              </Box>
            </Box>
          );
        }

        return (
          <Typography variant="body2">
            {params?.row?.swm_details === null
              ? "EE Components"
              : "Software Module"}
          </Typography>
        );
      },
    },
    {
      field: "ee_details",
      headerName: "EE Details",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;

        const details = params?.row?.ee_details;

        if (details) {
          return (
            <Box>
              <Typography variant="body2">
                <strong>Assembly No:</strong> {details.assembly_number || "-"}
              </Typography>

              <Typography variant="body2">
                <strong>Version:</strong> {details.version || "-"}
              </Typography>

              <Typography variant="body2">
                <strong>Description:</strong> {details.description || "-"}
              </Typography>
            </Box>
          );
        }

        return <Typography variant="body2">—</Typography>;
      },
    },
    {
      field: "software_details",
      headerName: "Software Details",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        if (params.row.isDetailRow) {
          const data = params.row.parentData;
          return (
            <Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Approved/Rejected By:
                </Typography>
                <Typography variant="body2">
                  Name: {data?.approved_rejected_by || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  Approved/Rejected Reason:
                </Typography>
                <Typography variant="body2">
                  {data?.approved_rejected_reason || "-"}
                </Typography>
              </Box>
            </Box>
          );
        }
        const details = params?.row?.swm_details;
        if (details) {
          return (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <strong> Name: </strong> {details?.name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Version:</strong> {details?.version || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Description: </strong> {details?.description || "-"}
              </Typography>
            </Box>
          );
        }
        return <Typography variant="body2">—</Typography>;
      },
    },
    {
      field: "requested_at",
      headerName: "Requested At",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;

        return (
          <Typography variant="body2">
            {params?.row?.requested_at || "-"}
          </Typography>
        );
      },
    },
    {
      field: "status_data",
      headerName: "Status",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;
        const status = params?.row?.status_data;
        const getStatusColor = () => {
          if (status === "PENDING") return "#000000";
          if (status === "APPROVED") return "#26890D";
          if (status === "REJECTED") return "#DC2626";
          return "#666";
        };

        return (
          <Typography
            variant="body2"
            sx={{
              color: getStatusColor(),
              fontStyle: "Open Sans",
              fontSize: "13px",
              lineHeight: "20px",
              letterSpacing: "0px",
            }}
          >
            {status || "-"}
          </Typography>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;
        return params?.row?.status === 300 ? (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5, fontWeight: "bold" }}
              onClick={(e) => {
                e.stopPropagation();
                handleApproveAddVcRequest(params?.row);
              }}
            >
              <DoneIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleRejectAddVcRequest(params?.row);
              }}
            >
              <BlockIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body2">—</Typography>
        );
      },
    },
  ];
  const generateTableRows = (tableRowsData) => {
    const rows = [];
    let index = 0;
    for (const item of tableRowsData || []) {
      rows.push({
        ...item,
        id: item.id || index,
        isMainRow: true,
      });

      if (expandedRows[item.id]) {
        rows.push({
          id: `${item.id}-detail`,
          isDetailRow: true,
          parentData: item,
        });
      }
      index += 1;
    }
    return rows;
  };

  return (
    <div>
      <Box
        sx={{
          height: 400,
          overflow: "auto",
          "& .MuiDataGrid-columnHeaders": {
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "#fff",
          },
        }}
      >
        <CustomDataGridV2
          handlePageChange={handleRowPerPageER}
          totalItems={editRequestCount}
          showPagination={true}
          rows={generateTableRows(tableRowsData)}
          page={page}
          rowsPerPage={rowsPerPage}
          columns={COLUMNS}
          isMultiLine={true}
          getRowHeight={(params) => (params.model.isDetailRow ? "auto" : 52)}
        />
      </Box>
      {openAddVcApproveDialog && (
        <CustomDialogBoxV2
          open={openAddVcApproveDialog}
          onClose={() => {
            setOpenAddVcApproveDialog(false);
            setAddReason("");
          }}
          title={`Action Reason`}
          onDisabled={addReason?.length < 3}
          successButtonName="Confirm"
          rejectButtonName="Cancel"
          otherButtonName="Re-direct"
          actionButtonStyle={"continuous"}
          onClickSuccess={() =>
            approveRequestAccess({
              params: selectedAddVcId,
              reason: addReason,
            })
          }
          onClickReject={() => {
            setOpenAddVcApproveDialog(false);
            setAddReason("");
          }}
          onOtherButtonClick={() => { }}
        >
          <Box sx={{ width: "400px" }}>
            <CustomFormTextFieldV2
              label="Reason"
              required={true}
              type="text"
              fldType="text"
              value={addReason}
              setValue={setAddReason}
              placeholder="Enter Reason"
            />
          </Box>
        </CustomDialogBoxV2>
      )}
      {openAddVcRejectDialog && (
        <CustomDialogBoxV2
          open={openAddVcRejectDialog}
          onClose={() => {
            setOpenAddVcRejectDialog(false);
            setAddReason("");
          }}
          onDisabled={addReason?.length < 3}
          title={`Action Reason`}
          successButtonName="Confirm"
          rejectButtonName="Cancel"
          otherButtonName="Re-direct"
          actionButtonStyle={"continuous"}
          onClickSuccess={() =>
            rejectRequestAccess({
              params: selectedAddVcId,
              reason: addReason,
            })
          }
          onClickReject={() => {
            setOpenAddVcRejectDialog(false);
            setAddReason("");
          }}
        >
          <Box sx={{ width: "400px" }}>
            <CustomFormTextFieldV2
              label="Reason"
              required={true}
              type="text"
              fldType="text"
              value={addReason}
              setValue={setAddReason}
              placeholder="Enter Reason"
            />
          </Box>
        </CustomDialogBoxV2>
      )}
    </div>
  );
}

ShowVarientRequestDetails.propTypes = {
  editRequestCount: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  tableRowsData: PropTypes.array,
  fetchEditRequests: PropTypes.func,
  handleRowPerPageER: PropTypes.func,
};

export default ShowVarientRequestDetails;
